/**
 * @fileoverview ChainResults
 * @description The report: an overall verdict, then every hop graded on its own.
 *
 *              The first hop that breaks is called out explicitly, because
 *              everything downstream of a break inherits the bad data and is
 *              not independent evidence — pointing at the first break is the
 *              difference between fixing the cause and debugging a symptom.
 */

import React, { useState } from 'react';
import {
  Alert, Box, Chip, Collapse, Divider, Grow, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  AlertTriangle, ArrowRight, CheckCircle2, ChevronDown, ChevronUp, Clock, HelpCircle, History,
} from 'lucide-react';
import { RawResponseButton } from './RawResponseViewer';

const NAVY = '#01426A';

const VERDICT_STYLE = {
  MATCH: { color: '#00875A', bg: '#ecfdf5', label: 'Everything agrees', Icon: CheckCircle2 },
  ID_DIFFERS: { color: '#0891b2', bg: '#ecfeff', label: 'Same people, different IDs', Icon: CheckCircle2 },
  FIELD_DIFFERS: { color: '#b45309', bg: '#fffbeb', label: 'Same people, a field disagrees', Icon: AlertTriangle },
  MISMATCH: { color: '#dc2626', bg: '#fef2f2', label: 'The chain breaks', Icon: AlertTriangle },
  INCOMPLETE: { color: '#b45309', bg: '#fffbeb', label: 'Could not compare every hop', Icon: HelpCircle },
  UNKNOWN: { color: '#64748b', bg: '#f8fafc', label: 'Not compared', Icon: HelpCircle },
};

const STATUS_COLOR = {
  MATCH: '#00875A',
  'ID DIFFERS': '#0891b2',
  'FIELD DIFFERS': '#b45309',
};

const describe = (member) => {
  if (!member) return '—';
  const tags = [member.deadhead && 'DH', member.hidden && 'HIDDEN'].filter(Boolean);
  return `${member.name || '(unnamed)'} (${member.crewId || '?'})${tags.length ? ` [${tags.join(' ')}]` : ''}`;
};

function VerdictBanner({ verdict, breaksAt }) {
  const style = VERDICT_STYLE[verdict] || VERDICT_STYLE.UNKNOWN;
  const { Icon } = style;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25, mb: 2.5, borderRadius: '14px',
        bgcolor: style.bg, border: `1px solid ${style.color}33`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Icon size={22} color={style.color} style={{ flexShrink: 0, marginTop: 2 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: style.color }}>
            {style.label}
          </Typography>
          {breaksAt ? (
            breaksAt.kind === 'field' ? (
              <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                {breaksAt.leftLabel} and {breaksAt.rightLabel} agree on who is on this flight but
                disagree about their details. Investigate <strong>{breaksAt.name}</strong> first —
                every hop after it inherits the same values.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                {breaksAt.leftLabel} and {breaksAt.rightLabel} hold different crew, so the change
                either never reached {breaksAt.rightLabel} or it is reading from a different roster
                instance. Investigate <strong>{breaksAt.name}</strong> first — every hop after it
                inherits the same bad data.
              </Typography>
            )
          ) : (
            <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
              {verdict === 'ID_DIFFERS'
                ? 'The sources do not share an ID space, so the same person carries a different ID at each stop. The seats themselves line up.'
                : verdict === 'INCOMPLETE'
                  ? 'At least one checkpoint could not be reached, so part of the chain is unproven.'
                  : 'Every checkpoint names the same crew on this leg.'}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function HopCard({ hop, defaultOpen, fieldLabels }) {
  const [open, setOpen] = useState(defaultOpen);
  const style = VERDICT_STYLE[hop.verdict] || VERDICT_STYLE.UNKNOWN;
  const { Icon } = style;
  const missing = hop.comparison.filter((r) => r.status.endsWith(' ONLY'));
  const fieldDiffs = hop.comparison.filter((r) => r.status === 'FIELD DIFFERS');
  const summary = [
    missing.length && `${missing.length} seat${missing.length > 1 ? 's' : ''} missing`,
    fieldDiffs.length && `${fieldDiffs.length} seat${fieldDiffs.length > 1 ? 's' : ''} with a field difference`,
  ].filter(Boolean).join(' · ');

  return (
    <Grow in timeout={320}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '14px', border: '1px solid #e5e7eb',
          borderLeft: `4px solid ${style.color}`, overflow: 'hidden', mb: 1.5,
        }}
      >
        <Box
          onClick={() => setOpen((o) => !o)}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
          }}
          sx={{ p: 2, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#fafbfc' } }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Chip size="small" label={hop.leftShort}
                sx={{ fontWeight: 800, fontSize: '0.68rem', bgcolor: '#eff6ff', color: NAVY }} />
              <ArrowRight size={14} color="#94a3b8" />
              <Chip size="small" label={hop.rightShort}
                sx={{ fontWeight: 800, fontSize: '0.68rem', bgcolor: '#eff6ff', color: NAVY }} />
              <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 0.5 }}>
                {hop.skippedReason || hop.note}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
              <Chip
                size="small" icon={<Icon size={12} />} label={hop.verdict.replace('_', ' ')}
                sx={{
                  bgcolor: style.bg, color: style.color, fontWeight: 800, fontSize: '0.68rem',
                  '& .MuiChip-icon': { color: style.color },
                }}
              />
              <IconButton size="small" tabIndex={-1} sx={{ color: '#94a3b8' }}>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </IconButton>
            </Stack>
          </Stack>

          {Boolean(summary) && !open && (
            <Typography variant="caption" sx={{ color: style.color, fontWeight: 600, display: 'block', mt: 0.75 }}>
              {summary}
            </Typography>
          )}
        </Box>

        <Collapse in={open}>
          <Divider />
          <Box sx={{ p: 2, pt: 1.5, overflowX: 'auto' }}>
            {hop.skippedReason ? (
              <Alert severity="warning" sx={{ borderRadius: '10px' }}>{hop.skippedReason}</Alert>
            ) : (
              <>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap mb={1.25}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                    CHECKED
                  </Typography>
                  {(hop.comparedFields || []).map((key) => (
                    <Chip
                      key={key} size="small" label={fieldLabels?.[key] || key}
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }}
                    />
                  ))}
                  {!hop.comparedFields?.length && (
                    <Typography variant="caption" color="text.secondary">
                      no fields are mapped on both sides of this hop
                    </Typography>
                  )}
                </Stack>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <Box component="thead">
                  <Box component="tr">
                    {['POS', hop.leftShort, hop.rightShort, 'STATUS'].map((heading) => (
                      <Box
                        key={heading} component="th"
                        sx={{
                          textAlign: 'left', py: 0.75, px: 1, fontSize: '0.66rem', fontWeight: 800,
                          letterSpacing: '0.06em', color: '#94a3b8', borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        {heading.toUpperCase()}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {hop.comparison.map((row, index) => {
                    const color = STATUS_COLOR[row.status] || '#dc2626';
                    const differences = row.differences || [];
                    return (
                      <React.Fragment key={index}>
                        <Box component="tr" sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                          <Box component="td" sx={{ py: 0.85, px: 1, fontSize: '0.74rem', fontWeight: 800, color: NAVY, borderBottom: differences.length ? 'none' : '1px solid #f1f5f9' }}>
                            {row.position || '—'}
                          </Box>
                          <Box component="td" sx={{ py: 0.85, px: 1, fontSize: '0.74rem', color: '#475569', borderBottom: differences.length ? 'none' : '1px solid #f1f5f9' }}>
                            {describe(row.left)}
                          </Box>
                          <Box component="td" sx={{ py: 0.85, px: 1, fontSize: '0.74rem', color: '#475569', borderBottom: differences.length ? 'none' : '1px solid #f1f5f9' }}>
                            {describe(row.right)}
                          </Box>
                          <Box component="td" sx={{ py: 0.85, px: 1, borderBottom: differences.length ? 'none' : '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color }}>
                              {row.status}
                            </Typography>
                          </Box>
                        </Box>
                        {Boolean(differences.length) && (
                          <Box component="tr">
                            <Box component="td" colSpan={4} sx={{ px: 1, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {differences.map((diff) => (
                                  <Tooltip
                                    key={`${diff.leftField}-${diff.rightField}`}
                                    title={
                                      diff.severity === 'info'
                                        ? 'Reported for context; does not fail the hop.'
                                        : 'Counts against this hop.'
                                    }
                                  >
                                    <Chip
                                      size="small"
                                      label={`${diff.label}: ${diff.leftValue} → ${diff.rightValue}`}
                                      sx={{
                                        height: 20, fontSize: '0.65rem', fontWeight: 600,
                                        bgcolor: diff.severity === 'info' ? '#ecfeff' : '#fffbeb',
                                        color: diff.severity === 'info' ? '#0891b2' : '#b45309',
                                        border: `1px solid ${diff.severity === 'info' ? '#0891b222' : '#b4530922'}`,
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Stack>
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Grow>
  );
}

function CheckpointSummary({ checkpoint }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75, borderRadius: '12px', border: '1px solid #e5e7eb',
        borderTop: `3px solid ${checkpoint.error ? '#dc2626' : '#00875A'}`, height: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2" fontWeight={800} color={NAVY} noWrap>
          {checkpoint.label}
        </Typography>
        <Chip size="small" label={checkpoint.short}
          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#eff6ff', color: NAVY }} />
      </Stack>
      {checkpoint.error ? (
        <Typography variant="caption" sx={{ color: '#dc2626', display: 'block', mt: 0.5 }}>
          {checkpoint.error}
        </Typography>
      ) : (
        <Typography variant="h6" fontWeight={800} color="#00875A" lineHeight={1.2} mt={0.5}>
          {checkpoint.crewCount} <Typography component="span" variant="caption" color="text.secondary">crew</Typography>
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
        <Tooltip title="Time this checkpoint took">
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Clock size={11} color="#94a3b8" />
            <Typography variant="caption" color="text.secondary">{checkpoint.durationMs}ms</Typography>
          </Stack>
        </Tooltip>
        {checkpoint.lastUpdated && (
          <Typography variant="caption" color="text.secondary" noWrap>
            written {checkpoint.lastUpdated}
          </Typography>
        )}
      </Stack>
      {checkpoint.detail && (
        <Typography
          noWrap
          sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: '#94a3b8', mt: 0.5 }}
        >
          {checkpoint.detail}
        </Typography>
      )}
      <Box mt={0.5} ml={-0.75}>
        <RawResponseButton raw={checkpoint.raw} title={checkpoint.label} detail={checkpoint.detail} />
      </Box>
    </Paper>
  );
}

const ChainResults = ({ result, fieldLabels }) => {
  const [supersededOpen, setSupersededOpen] = useState(false);
  if (!result) return null;

  const superseded = (result.checkpoints || []).flatMap((c) =>
    (c.superseded || []).map((m) => ({ ...m, source: c.short })),
  );

  return (
    <Box>
      <VerdictBanner verdict={result.verdict} breaksAt={result.breaksAt} />

      <Box
        sx={{
          display: 'grid', gap: 1.5, mb: 2.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' },
        }}
      >
        {(result.checkpoints || []).map((checkpoint) => (
          <CheckpointSummary key={checkpoint.id} checkpoint={checkpoint} />
        ))}
      </Box>

      <Typography variant="subtitle2" fontWeight={800} color={NAVY} mb={1}>
        Hop by hop
      </Typography>
      {(result.hops || []).map((hop, index) => (
        <HopCard
          key={hop.name}
          hop={hop}
          fieldLabels={fieldLabels}
          defaultOpen={['MISMATCH', 'FIELD_DIFFERS'].includes(hop.verdict) || index === 0}
        />
      ))}

      {Boolean(superseded.length) && (
        <Paper elevation={0} sx={{ mt: 1.5, borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box
            onClick={() => setSupersededOpen((o) => !o)}
            role="button" tabIndex={0} aria-expanded={supersededOpen}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSupersededOpen((o) => !o); }
            }}
            sx={{ p: 1.75, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <History size={15} color="#64748b" />
              <Typography variant="subtitle2" fontWeight={700} color="#475569">
                {superseded.length} superseded row{superseded.length > 1 ? 's' : ''} from earlier writes
              </Typography>
            </Stack>
            {supersededOpen ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
          </Box>
          <Collapse in={supersededOpen}>
            <Divider />
            <Box sx={{ p: 1.75, bgcolor: '#f8fafc' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                The landing table kept these older assignments. A stale timestamp means the change
                never arrived, which is a different defect from receiving the wrong crew member.
              </Typography>
              {superseded.map((member, index) => (
                <Typography key={index} sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#475569' }}>
                  {member.updated || '—'} · {member.source} · {(member.position || '??')} · {describe(member)}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default ChainResults;
